import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { Icon } from "@iconify/react";
import { IMAGES } from "@/assets/images";
import { useLang } from "@/i18n";

const slides = [
  { img: IMAGES.HERO_DARK_3, pos: "60% 20%", tag: "KOL MARKETING" },
  { img: IMAGES.HERO_DARK_5, pos: "55% 15%", tag: "GLOBAL REACH" },
  { img: IMAGES.HERO_DARK_9, pos: "60% center", tag: "BRAND GROWTH" },
  { img: IMAGES.HERO_DARK_4, pos: "58% 10%", tag: "INFLUENCER NETWORK" },
];

const ease = [0.4, 0, 0.2, 1];

export default function Hero() {
  const { t } = useLang();
  const stats = t.hero.stats;
  const [current, setCurrent] = useState(0);
  const [progress, setProgress] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sectionRef = useRef<HTMLElement>(null);

  const { scrollY } = useScroll();
  const yParallax = useTransform(scrollY, [0, 800], [0, 120]);
  const opacityContent = useTransform(scrollY, [0, 400], [1, 0]);

  const scrollTo = (href: string) =>
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });

  const startTimer = () => {
    setProgress(0);
    if (intervalRef.current) clearInterval(intervalRef.current);
    let p = 0;
    intervalRef.current = setInterval(() => {
      p += 1;
      setProgress(p);
      if (p >= 100) {
        setCurrent((c) => (c + 1) % slides.length);
        p = 0;
        setProgress(0);
      }
    }, 70);
  };

  useEffect(() => {
    startTimer();
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [current]);

  return (
    <section
      ref={sectionRef}
      id="hero"
      className="relative w-full overflow-hidden"
      style={{ height: "100svh", minHeight: 700, background: "#050505" }}
    >
      {/* ── Background slideshow with parallax ── */}
      <AnimatePresence mode="sync">
        <motion.div
          key={current}
          className="absolute inset-0"
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 2.2, ease }}
          style={{ y: yParallax }}
        >
          <img
            src={slides[current].img}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            style={{ objectPosition: slides[current].pos }}
          />
          {/* Multi-layer gradient overlay */}
          <div className="absolute inset-0" style={{
            background: "linear-gradient(105deg, rgba(5,5,5,0.97) 0%, rgba(5,5,5,0.82) 30%, rgba(5,5,5,0.35) 60%, rgba(5,5,5,0.1) 100%)"
          }} />
          <div className="absolute inset-0" style={{
            background: "linear-gradient(180deg, rgba(5,5,5,0.55) 0%, transparent 22%, transparent 65%, rgba(5,5,5,0.95) 100%)"
          }} />
          {/* Vignette */}
          <div className="absolute inset-0" style={{
            background: "radial-gradient(ellipse at 70% 50%, transparent 40%, rgba(5,5,5,0.6) 100%)"
          }} />
        </motion.div>
      </AnimatePresence>

      {/* ── Film grain overlay ── */}
      <div className="absolute inset-0 pointer-events-none z-[1]" style={{
        opacity: 0.028,
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='g'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23g)' opacity='1'/%3E%3C/svg%3E")`,
        backgroundSize: "300px 300px",
      }} />

      {/* ── Animated scan line ── */}
      <motion.div
        className="absolute left-0 right-0 pointer-events-none z-[1]"
        style={{ height: 1, background: "linear-gradient(90deg, transparent, rgba(244,68,12,0.15), transparent)" }}
        animate={{ top: ["0%", "100%"] }}
        transition={{ duration: 6, repeat: Infinity, ease: "linear", repeatDelay: 2 }}
      />

      {/* ── Vertical accent line ── */}
      <div className="absolute z-10 hidden lg:block" style={{ left: "3rem", top: 0, bottom: 0, width: 1, background: "linear-gradient(180deg, transparent, rgba(255,255,255,0.06) 30%, rgba(255,255,255,0.06) 70%, transparent)" }} />

      {/* ── Main content ── */}
      <motion.div
        className="relative z-10 h-full flex flex-col justify-between max-w-7xl mx-auto hero-main-pad"
        style={{ opacity: opacityContent }}
      >
        {/* Copy block */}
        <div style={{ maxWidth: 640 }}>

          {/* Slide tag badge */}
          <AnimatePresence mode="wait">
            <motion.div
              key={current}
              initial={{ opacity: 0, x: -12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 12 }}
              transition={{ duration: 0.5, ease }}
              className="flex items-center gap-3 mb-8"
            >
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#f4440c" }} />
              <span style={{
                fontSize: "0.7rem",
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.45)",
                fontWeight: 500,
              }}>
                {slides[current].tag}
              </span>
            </motion.div>
          </AnimatePresence>

          {/* Eyebrow */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.15, ease }}
            className="flex items-center gap-4 mb-7"
          >
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.8, delay: 0.3, ease }}
              style={{ transformOrigin: "left", width: 44, height: 1.5, background: "#f4440c", flexShrink: 0 }}
            />
            <span style={{
              color: "rgba(255,255,255,0.58)",
              fontSize: "0.82rem",
              letterSpacing: "0.14em",
              fontWeight: 400,
              textTransform: "uppercase",
            }}>
              {t.hero.tag}
            </span>
          </motion.div>

          {/* Headline — large impact */}
          <div style={{ overflow: "hidden", marginBottom: "0.5rem" }}>
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 1.1, delay: 0.3, ease }}
            >
              <h1 style={{
                fontFamily: "'Inter Tight', sans-serif",
                fontSize: "clamp(3rem, 7.5vw, 7.2rem)",
                fontWeight: 700,
                lineHeight: 0.95,
                letterSpacing: "-0.04em",
                color: "#fff",
              }}>
                {t.hero.headline1}
              </h1>
            </motion.div>
          </div>
          <div style={{ overflow: "hidden", marginBottom: "1.8rem" }}>
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 1.1, delay: 0.45, ease }}
            >
              <h1 style={{
                fontFamily: "'Inter Tight', sans-serif",
                fontSize: "clamp(3rem, 7.5vw, 7.2rem)",
                fontWeight: 700,
                lineHeight: 0.95,
                letterSpacing: "-0.04em",
                color: "transparent",
                WebkitTextStroke: "2px #f4440c",
                display: "inline-block",
              }}>
                {t.hero.headline2}
              </h1>
            </motion.div>
          </div>

          {/* Sub copy */}
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.7, ease }}
            style={{
              color: "rgba(255,255,255,0.58)",
              fontSize: "1.05rem",
              lineHeight: 1.85,
              letterSpacing: "0.01em",
              maxWidth: 440,
              fontWeight: 400,
              marginBottom: "2.8rem",
            }}
          >
            {t.hero.sub}
          </motion.p>

          {/* CTA row */}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.85, ease }}
            className="flex items-center gap-6"
          >
            <button
              onClick={() => scrollTo("#contact")}
              style={{
                padding: "1rem 2.6rem",
                background: "#f4440c",
                color: "#fff",
                fontSize: "0.88rem",
                fontWeight: 600,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                border: "none",
                cursor: "pointer",
                transition: "all 0.35s ease",
                position: "relative",
                overflow: "hidden",
              }}
              onMouseEnter={e => {
                const el = e.currentTarget as HTMLElement;
                el.style.background = "#d93a0a";
                el.style.transform = "translateY(-1px)";
                el.style.boxShadow = "0 8px 30px rgba(244,68,12,0.4)";
              }}
              onMouseLeave={e => {
                const el = e.currentTarget as HTMLElement;
                el.style.background = "#f4440c";
                el.style.transform = "translateY(0)";
                el.style.boxShadow = "none";
              }}
            >
              {t.hero.cta}
            </button>

            <button
              onClick={() => scrollTo("#services")}
              className="flex items-center gap-3"
              style={{
                color: "rgba(255,255,255,0.52)",
                fontSize: "0.88rem",
                fontWeight: 400,
                letterSpacing: "0.06em",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 0,
                transition: "color 0.3s",
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = "rgba(255,255,255,0.9)"; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = "rgba(255,255,255,0.52)"; }}
            >
              <span>{t.nav.services}</span>
              <motion.span
                animate={{ x: [0, 4, 0] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
              >
                <Icon icon="mdi:arrow-right" style={{ fontSize: 16, display: "block" }} />
              </motion.span>
            </button>
          </motion.div>
        </div>

        {/* Bottom: stats + slide controls */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.05, ease }}
          className="hero-bottom-row"
        >
          {/* Stats row */}
          <div className="hero-stats-row">
            {stats.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1.1 + i * 0.07, ease }}
                className="flex flex-col"
                style={{
                  paddingRight: "1.8rem",
                  marginRight: i < stats.length - 1 ? "1.8rem" : 0,
                  borderRight: i < stats.length - 1 ? "1px solid rgba(255,255,255,0.1)" : "none",
                  maxWidth: 140,
                }}
              >
                <span style={{
                  fontFamily: "'Inter Tight', sans-serif",
                  fontSize: "clamp(1.5rem, 2.5vw, 2.1rem)",
                  fontWeight: 700,
                  color: "#fff",
                  lineHeight: 1,
                  letterSpacing: "-0.03em",
                }}>
                  {s.value}
                </span>
                <span style={{
                  fontSize: "0.72rem",
                  letterSpacing: "0.06em",
                  color: "rgba(255,255,255,0.38)",
                  fontWeight: 400,
                  marginTop: "0.45rem",
                  lineHeight: 1.4,
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                }}>
                  {s.label}
                </span>
              </motion.div>
            ))}
          </div>

          {/* Slide controls */}
          <div className="flex flex-col items-end gap-3">
            <div className="flex items-center gap-2">
              {slides.map((_, i) => (
                <button
                  key={i}
                  onClick={() => { setCurrent(i); setProgress(0); }}
                  style={{
                    height: 2,
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: 0,
                    position: "relative",
                    width: i === current ? 56 : 20,
                    transition: "width 0.4s ease",
                    overflow: "hidden",
                  }}
                  aria-label={`Slide ${i + 1}`}
                >
                  {i === current && (
                    <span style={{
                      position: "absolute", top: 0, left: 0,
                      height: "100%", width: `${progress}%`,
                      background: "#f4440c",
                      transition: "width 0.07s linear",
                    }} />
                  )}
                  {i !== current && (
                    <span style={{ position: "absolute", inset: 0, background: "rgba(255,255,255,0.18)" }} />
                  )}
                </button>
              ))}
            </div>
            <span style={{
              fontSize: "0.68rem",
              letterSpacing: "0.18em",
              color: "rgba(255,255,255,0.25)",
              fontWeight: 400,
              fontFamily: "'Inter Tight', sans-serif",
            }}>
              {String(current + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")}
            </span>
          </div>
        </motion.div>
      </motion.div>

      {/* ── Scroll indicator ── */}
      <motion.div
        className="absolute bottom-10 left-1/2 z-10 flex flex-col items-center gap-2"
        style={{ transform: "translateX(-50%)" }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 0.8 }}
      >
        <motion.div
          animate={{ scaleY: [1, 1.4, 1], opacity: [0.3, 0.8, 0.3] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          style={{ width: 1, height: 48, background: "linear-gradient(180deg, rgba(244,68,12,0.8), transparent)", transformOrigin: "top" }}
        />
      </motion.div>
    </section>
  );
}
